import { useSearchParams, Navigate } from 'react-router-dom';
import { typography, shadow } from '@/shared/theme/tokens';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { usePermissions } from '@/entities/rbac/hooks/usePermissions';
import { ROUTES } from '@/shared/config/constants';
import { SvgTemplatesManager } from '@/features/svgTemplates/ui/SvgTemplatesManager';

// Global (cross-deployment) admin settings. Sectioned like deployment settings;
// the first section is the SVG template catalog. More sections can be added here.
const SECTIONS = [
  { key: 'svg-templates', label: 'SVG Templates', icon: 'schema' },
];
const VALID = SECTIONS.map((s) => s.key);

export default function GlobalSettingsPage() {
  const c = useThemeColors();
  const isDark = useIsDark();
  const { isMobile } = useBreakpoint();
  const { isRipikAdmin } = usePermissions();
  const [searchParams] = useSearchParams();

  // ripik-admin only.
  if (!isRipikAdmin) return <Navigate to={ROUTES.DEPLOYMENTS} replace />;

  const raw = searchParams.get('section') ?? 'svg-templates';
  const active = VALID.includes(raw) ? raw : 'svg-templates';
  const meta = SECTIONS.find((s) => s.key === active);

  return (
    <div style={{ fontFamily: typography.fontFamily.body, width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ marginBottom: isMobile ? '12px' : '20px' }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? '16px' : '20px', fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading, letterSpacing: typography.letterSpacing.tight }}>
          Settings
        </h1>
        <p style={{ margin: '2px 0 0', fontSize: typography.fontSize.base, color: c.textSubtle }}>Global, cross-deployment configuration.</p>
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <SettingsIsland label={meta.label} icon={meta.icon} c={c} isDark={isDark} isMobile={isMobile}>
          {active === 'svg-templates' && <SvgTemplatesManager canManage={isRipikAdmin} />}
        </SettingsIsland>
      </div>
    </div>
  );
}

function SettingsIsland({ label, icon, c, isDark, isMobile, children }) {
  return (
    <div style={{
      background: isDark ? 'rgba(255,255,255,0.025)' : '#fff',
      border: `1px solid ${c.border}`,
      borderRadius: isMobile ? 10 : 14,
      boxShadow: shadow.panel,
      overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: isMobile ? '10px 14px' : '14px 20px',
        borderBottom: `1px solid ${c.border}`,
        background: isDark ? 'rgba(255,255,255,0.02)' : c.bgSubtle, flexShrink: 0,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 17, color: c.textSubtle }}>{icon}</span>
        <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.body, letterSpacing: typography.letterSpacing.normal }}>{label}</span>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: isMobile ? '12px' : '20px' }}>
        {children}
      </div>
    </div>
  );
}
